import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { authFetch } from '../../services/api';
import SubscriptionStatus from '../SubscriptionStatus';
/* ─────────────────────────────────────────────
   SubscriptionSection — Paramètres abonnement
   Page dédiée avec hero, stats, paliers, comparatif
   ───────────────────────────────────────────── */
export default function SubscriptionSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);

  const loadStatus = useCallback(async () => {
    try {
      const res = await authFetch('/api/subscriptions/status');
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch {
      // silence
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const res = await authFetch('/api/subscriptions/my-requests');
      if (res.ok) {
        const data = await res.json();
        setHistory(Array.isArray(data) ? data : []);
      }
    } catch {
      // silence
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadHistory();
  }, [loadStatus, loadHistory]);



  // ── États ──
  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }} />
        <p className="text-muted mt-3 small">Chargement de votre abonnement...</p>
      </div>
    );
  }

  // Toujours afficher la section même si system_enabled est false
  if (!status) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }} />
        <p className="text-muted mt-3 small">Chargement de votre abonnement...</p>
      </div>
    );
  }

  const isPremium = status.tier === 'premium';
  const tracksUsed = status.tracks_used_this_month || 0;
  const limit = status.limit_this_month || (isPremium ? (status.premium_track_limit || 30) : (status.free_track_limit || 3));
  const remaining = status.tracks_remaining ?? Math.max(0, limit - tracksUsed);
  const progressPct = Math.min(100, Math.round((tracksUsed / limit) * 100));
  const price = (status.premium_price_ariary || 0).toLocaleString();
  const premCommission = Math.round((status.premium_commission_rate || 0.15) * 100);
  const freeCommission = Math.round((status.free_commission_rate || 0.20) * 100);
  const premTracks = status.premium_track_limit || 30;
  const freeTracks = status.free_track_limit || 3;

  return (
    <div className="settings-section" style={{ padding: 0, overflow: 'hidden' }}>
      {/* ═══════════ HERO — STATUT ACTUEL ═══════════ */}
      <div style={{
        background: isPremium
          ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #b45309 100%)'
          : 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
        padding: '2.5rem 2rem',
        color: '#fff',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Décoration */}
        <div style={{
          position: 'absolute', top: '-60px', right: '-60px',
          width: '200px', height: '200px', borderRadius: '50%',
          background: 'rgba(255,255,255,0.06)',
        }} />
        <div style={{
          position: 'absolute', bottom: '-40px', left: '-40px',
          width: '150px', height: '150px', borderRadius: '50%',
          background: 'rgba(255,255,255,0.04)',
        }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: '1rem',
          }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                background: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                padding: '6px 14px', borderRadius: '100px',
                fontSize: '0.75rem', fontWeight: 600,
                letterSpacing: '1px', textTransform: 'uppercase',
                marginBottom: '1rem',
              }}>
                {isPremium
                  ? <><i className="fa-solid fa-crown" /> Plan Premium</>
                  : <><i className="fa-solid fa-leaf" /> Plan Free</>
                }
              </div>

              <h2 style={{ margin: 0, fontWeight: 800, fontSize: '1.75rem', lineHeight: 1.2 }}>
                {isPremium
                  ? 'Vous êtes Premium'
                  : 'Démarrez avec le plan Free'
                }
              </h2>

              {isPremium && (status.effective_expires_at || status.subscription_expires_at) && (
                <p style={{ margin: '0.5rem 0 0', opacity: 0.85, fontSize: '0.85rem' }}>
                  <i className="fa-regular fa-calendar me-1" />
                  Actif jusqu'au {new Date(status.effective_expires_at || status.subscription_expires_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              )}


            </div>                {!isPremium && (
              <Link to="/dashboard/premium" className="btn btn-light fw-bold rounded-pill px-4 py-2" style={{
                  color: '#92400e', whiteSpace: 'nowrap',
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
              }}>
                <i className="fa-solid fa-crown" /> Passer Premium — {price} Ar/mois
              </Link>
            )}

            {isPremium && status.is_expiring_soon && (
              <Link to="/dashboard/premium" className="btn btn-light fw-bold rounded-pill px-4">
                🔄 Renouveler
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* ═══════════ STATS — QUOTAS ═══════════ */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1rem',
        padding: '1.5rem 2rem',
        background: '#fafbfc',
        borderBottom: '1px solid #e9ecef',
      }}>
        {/* Tracks */}
        <div style={{
          background: '#fff', borderRadius: '14px', padding: '1.25rem',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '10px',
                background: isPremium ? 'rgba(245,158,11,0.12)' : 'rgba(13,110,253,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: isPremium ? '#d97706' : '#0d6efd',
              }}>
                <i className="fa-solid fa-music" />
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tracks</div>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#212529' }}>{isPremium ? `${premTracks} / mois` : `${freeTracks} / mois`}</div>
              </div>
            </div>
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: remaining === 0 ? '#dc3545' : '#212529' }}>
              {tracksUsed}/{limit}
            </span>
          </div>
          <div style={{ height: '6px', borderRadius: '3px', background: '#e9ecef', overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: '3px',
              width: `${progressPct}%`,
              background: remaining === 0
                ? '#dc3545'
                : isPremium
                  ? 'linear-gradient(90deg, #f59e0b, #d97706)'
                  : 'linear-gradient(90deg, #0d6efd, #0b5ed7)',
              transition: 'width 0.5s ease',
            }} />
          </div>
          <div style={{ fontSize: '0.7rem', marginTop: '6px', color: remaining === 0 ? '#dc3545' : '#6c757d', fontWeight: remaining === 0 ? 600 : 400 }}>
            {remaining === 0
              ? isPremium
                ? '🚫 Quota épuisé — attendez le prochain mois'
                : '🚫 Quota épuisé — passez Premium pour continuer'
              : `${remaining} track${remaining > 1 ? 's' : ''} restante${remaining > 1 ? 's' : ''} ce mois`
            }
          </div>
        </div>

        {/* Albums — Illimité pour tout le monde */}
        <div style={{
          background: '#fff', borderRadius: '14px', padding: '1.25rem',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '10px',
                background: 'rgba(13,202,240,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#0dcaf0',
              }}>
                <i className="fa-solid fa-compact-disc" />
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Albums</div>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#212529' }}>Illimité</div>
              </div>
            </div>
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#28a745' }}>
              <i className="fa-solid fa-infinity"></i>
            </span>
          </div>
          <div style={{ fontSize: '0.7rem', color: '#6c757d' }}>
            Créez et publiez autant d'albums que vous le souhaitez
          </div>
        </div>

        {/* Commission */}
        <div style={{
          background: '#fff', borderRadius: '14px', padding: '1.25rem',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '10px',
                background: isPremium ? 'rgba(245,158,11,0.12)' : 'rgba(108,117,125,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: isPremium ? '#d97706' : '#6c757d',
              }}>
                <i className="fa-solid fa-percent" />
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Commission</div>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#212529' }}>
                  {isPremium ? `${premCommission}%` : `${freeCommission}%`}
                </div>
              </div>
            </div>
            <span style={{
              fontSize: '0.7rem', fontWeight: 700,
              padding: '3px 10px', borderRadius: '100px',
              background: isPremium ? 'rgba(40,167,69,0.1)' : 'rgba(108,117,125,0.1)',
              color: isPremium ? '#28a745' : '#6c757d',
            }}>
              {isPremium && freeCommission > premCommission ? `${String(Math.round((1 - premCommission / freeCommission) * 100))}% vs Free` : 'Standard'}
            </span>
          </div>
          <div style={{ fontSize: '0.7rem', color: '#6c757d' }}>
            {isPremium
              ? `Gardez plus de vos revenus — commission réduite à ${premCommission}%`
              : `Passez Premium pour réduire votre commission à ${premCommission}%`
            }
          </div>
        </div>

        {/* Paiement */}
        <div style={{
          background: '#fff', borderRadius: '14px', padding: '1.25rem',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '10px',
                background: isPremium ? 'rgba(245,158,11,0.12)' : 'rgba(108,117,125,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: isPremium ? '#d97706' : '#6c757d',
              }}>
                <i className="fa-solid fa-wallet" />
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Paiement</div>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#212529' }}>
                  {isPremium ? 'J+15' : 'J+30'}
                </div>
              </div>
            </div>
            <span style={{
              fontSize: '0.7rem', fontWeight: 700,
              padding: '3px 10px', borderRadius: '100px',
              background: isPremium ? 'rgba(40,167,69,0.1)' : 'rgba(108,117,125,0.1)',
              color: isPremium ? '#28a745' : '#6c757d',
            }}>
              {isPremium ? `${premCommission <= freeCommission ? `−${Math.round((1 - premCommission / freeCommission) * 100)}%` : ''}` : 'Standard'}
            </span>
          </div>
          <div style={{ fontSize: '0.7rem', color: '#6c757d' }}>
            {isPremium
              ? 'Soyez payé en 15 jours au lieu de 30'
              : 'Passez Premium pour être payé en 15 jours'
            }
          </div>
        </div>
      </div>

      {/* ═══════════ TABLEAU COMPARATIF ═══════════ */}
      <div style={{ padding: '2rem' }}>
        <h3 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: '1.15rem', fontWeight: 700, color: '#212529',
          marginBottom: '0.25rem',
        }}>
          Comparatif complet des plans
        </h3>
        <p style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '1.5rem' }}>
          Tout ce que vous obtenez avec chaque formule
        </p>

        <div style={{
          overflowX: 'auto',
          borderRadius: '14px',
          border: '1px solid #e9ecef',
          WebkitOverflowScrolling: 'touch',
        }}>
          <table style={{
            width: '100%', borderCollapse: 'collapse',
            minWidth: '500px',
            fontSize: '0.85rem',
          }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th style={{ padding: '14px 18px', textAlign: 'left', fontWeight: 700, color: '#495057', borderBottom: '2px solid #e9ecef' }}>
                  Fonctionnalité
                </th>
                <th style={{ padding: '14px 18px', textAlign: 'center', fontWeight: 700, color: '#6c757d', borderBottom: '2px solid #e9ecef', width: '25%' }}>
                  Free
                </th>
                <th style={{ padding: '14px 18px', textAlign: 'center', fontWeight: 700, color: '#d97706', borderBottom: '2px solid #f59e0b', width: '25%', background: 'rgba(245,158,11,0.06)' }}>
                  💎 Premium
                </th>
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'Tracks / mois', free: String(freeTracks), premium: String(premTracks) },
                { label: 'Albums actifs', free: 'Illimité', premium: 'Illimité' },
                { label: 'Singles gratuits', free: '🔒', premium: '✅', locked: !isPremium, lock: !isPremium },
                { label: 'Streaming HLS', free: '64k', premium: '64k + 96k', locked: !isPremium, lock: !isPremium },
                { label: 'Paroles LRC', free: '🔒', premium: '✅', locked: !isPremium, lock: !isPremium },
                { label: 'Analytics avancés', free: '🔒', premium: 'Avancé', locked: !isPremium, lock: !isPremium },
                { label: 'Commission', free: `${freeCommission}%`, premium: `${premCommission}%`, premiumHighlight: true, premiumSuffix: freeCommission > premCommission ? `−${Math.round((1 - premCommission / freeCommission) * 100)}%` : '' },
                { label: 'Paiement', free: 'J+30', premium: 'J+15', premiumHighlight: true, premiumSuffix: '−15j' },
                { label: 'Mention "Powered by"', free: 'Affichée', premium: 'Retirée', locked: !isPremium, lock: !isPremium },
                { label: 'Support', free: 'Standard 48h', premium: 'Prioritaire', locked: !isPremium },
                { label: 'Bot Telegram', free: 'Basique', premium: 'Avancé', locked: !isPremium, lock: !isPremium },
              ].map((row, i) => (
                <tr key={i} style={{
                  borderBottom: '1px solid #f0f0f0',
                  opacity: row.locked ? 0.5 : 1,
                }}>
                  <td style={{ padding: '12px 18px', fontWeight: 500, color: '#495057' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      {row.label}
                      {row.lock && <span style={{ color: '#d97706', fontSize: '0.7rem' }}>🔒</span>}
                    </span>
                  </td>
                  <td style={{ padding: '12px 18px', textAlign: 'center', color: '#6c757d' }}>
                    {row.free}
                  </td>
                  <td style={{
                    padding: '12px 18px', textAlign: 'center',
                    fontWeight: row.premiumHighlight ? 700 : 400,
                    color: row.premiumHighlight ? '#d97706' : '#212529',
                    background: row.premiumHighlight ? 'rgba(245,158,11,0.04)' : 'rgba(245,158,11,0.02)',
                  }}>
                    {row.premium}
                    {row.premiumSuffix && (
                      <span style={{ fontSize: '0.6rem', color: '#d97706', fontWeight: 600, marginLeft: '4px' }}>{row.premiumSuffix}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* CTA final */}
        {!isPremium && (
          <div style={{
            marginTop: '1.5rem',
            background: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(217,119,6,0.04))',
            border: '1px solid rgba(245,158,11,0.15)',
            borderRadius: '14px', padding: '1.5rem 2rem',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
              <i className="fa-solid fa-crown" style={{ color: '#d97706' }} />
            </div>
            <h4 style={{ fontWeight: 700, color: '#92400e', marginBottom: '0.25rem' }}>
              Prêt à passer au niveau supérieur ?
            </h4>
            <p style={{ fontSize: '0.85rem', color: '#b45309', marginBottom: '1rem', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
              {`Économisez ${String(Math.round((1 - (status.premium_commission_rate || 0.15) / (status.free_commission_rate || 0.20)) * 100))}% de commission, débloquez ${premTracks} tracks/mois, albums illimités, paroles LRC, streaming haute qualité et bien plus.`}
            </p>
            <Link to="/dashboard/premium" className="btn" style={{
                padding: '14px 36px', textDecoration: 'none',
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                color: '#fff', fontWeight: 700, fontSize: '0.95rem',
                border: 'none', borderRadius: '100px',
                display: 'inline-flex', alignItems: 'center', gap: '10px',
                boxShadow: '0 6px 20px rgba(245,158,11,0.3)',
              }}>
              <i className="fa-solid fa-crown" /> Passer Premium — {price} Ar/mois
            </Link>
          </div>
        )}
      </div>

      {/* ═══════════ HISTORIQUE DES PAIEMENTS ═══════════ */}
      {history.length > 0 && (
        <div style={{ padding: '0 2rem 2rem' }}>
          <h3 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '1.15rem', fontWeight: 700, color: '#212529',
            marginBottom: '0.25rem',
          }}>
            Historique des paiements
          </h3>
          <p style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '1rem' }}>
            Toutes vos demandes d'abonnement Premium
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {history.map((item) => {
              const statusColor = item.status === 'approved'
                ? { bg: 'rgba(40,167,69,0.1)', text: '#28a745', icon: 'fa-check-circle', label: 'Approuvé' }
                : item.status === 'rejected'
                  ? { bg: 'rgba(220,53,69,0.1)', text: '#dc3545', icon: 'fa-times-circle', label: 'Rejeté' }
                  : { bg: 'rgba(245,158,11,0.1)', text: '#d97706', icon: 'fa-clock', label: 'En attente' };

              return (
                <div key={item.id} style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '14px',
                  padding: '1rem 1.25rem',
                  background: '#fff',
                  borderRadius: '12px',
                  border: '1px solid #e9ecef',
                  transition: 'all 0.2s',
                }}>
                  {/* Icône de statut */}
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '12px',
                    background: statusColor.bg,
                    color: statusColor.text,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    fontSize: '1.1rem',
                  }}>
                    <i className={`fa-solid ${statusColor.icon}`} />
                  </div>

                  {/* Contenu */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '6px' }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#212529' }}>
                          Abonnement Premium — {Number(item.amount || 0).toLocaleString()} Ar
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '2px' }}>
                          <i className="fa-regular fa-calendar me-1" />
                          {new Date(item.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>

                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        padding: '4px 12px', borderRadius: '100px',
                        background: statusColor.bg,
                        color: statusColor.text,
                        fontSize: '0.75rem', fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}>
                        <i className={`fa-solid ${statusColor.icon}`} style={{ fontSize: '0.65rem' }} />
                        {statusColor.label}
                      </span>
                    </div>

                    {/* Code unique */}
                    <div style={{
                      marginTop: '8px',
                      display: 'inline-flex', alignItems: 'center',
                      gap: '6px',
                      padding: '4px 10px',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      color: '#495057',
                      letterSpacing: '1px',
                    }}>
                      <span style={{ color: '#6c757d', fontSize: '0.65rem' }}>CODE :</span>
                      {item.unique_code}
                    </div>

                    {/* Notes admin */}
                    {item.admin_notes && (
                      <div style={{
                        marginTop: '8px',
                        fontSize: '0.75rem',
                        color: '#6c757d',
                        fontStyle: 'italic',
                        padding: '6px 10px',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                      }}>
                        <i className="fa-regular fa-note-sticky me-1" />
                        {item.admin_notes}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}


    </div>
  );
}
